/**
 * VIP权限提示组件
 * 当用户权限不足时显示升级提示
 */

import type React from 'react';
import { useVIPContext } from '../../providers/VIPProvider.js';
import type { VIPPermissions } from '../../types/vip.js';
import { VIPBadge } from './VIPBadge.js';

interface PermissionGuardProps {
  feature: keyof VIPPermissions;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * 权限守卫组件
 * 如果有权限则显示子组件,否则显示升级提示
 */
export function PermissionGuard({ feature, children, fallback }: PermissionGuardProps) {
  const { hasPermission, permissions, getVIPLevel } = useVIPContext();

  if (!permissions) {
    return <div className="text-gray-500">加载权限信息中...</div>;
  }

  if (hasPermission(feature)) {
    return <>{children}</>;
  }

  // 使用自定义fallback
  if (fallback) {
    return <>{fallback}</>;
  }

  // 默认的权限不足提示
  const level = getVIPLevel();
  const featureNames: Record<keyof VIPPermissions, string> = {
    text_chat: '文字聊天',
    voice_chat: '语音聊天',
    phone_call: '语音通话',
    knowledge_base: '知识库',
    long_memory: '长期记忆',
    virtual_ring: '虚拟戒指',
    virtual_gateway: '虚拟网关',
    mcp_tools: 'MCP工具',
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <VIPBadge level={level.level} code={level.code as any} size="large" />
      <p className="mt-4 text-lg font-semibold text-gray-800">
        该功能需要VIP会员权限
      </p>
      <p className="mt-2 text-sm text-gray-600">
        开通VIP即可使用{featureNames[feature]}等高级功能
      </p>
      <button
        className="mt-4 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-2 text-white font-semibold hover:from-yellow-600 hover:to-orange-600"
        onClick={() => {
          // 跳转到VIP购买页面
          window.location.href = '/vip/upgrade';
        }}
      >
        立即开通VIP
      </button>
    </div>
  );
}

/**
 * 限制提示组件
 * 当用户达到使用限制时显示提示
 */
interface LimitGuardProps {
  feature: 'role_slots' | 'memory_count' | 'kb_file_count';
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function LimitGuard({ feature, children, fallback }: LimitGuardProps) {
  const { checkLimit, permissions } = useVIPContext();

  if (!permissions) {
    return <div className="text-gray-500">加载权限信息中...</div>;
  }

  if (checkLimit(feature)) {
    return <>{children}</>;
  }

  // 使用自定义fallback
  if (fallback) {
    return <>{fallback}</>;
  }

  // 默认的达到限制提示
  const limit = permissions.limits[feature];

  const featureNames: Record<typeof feature, string> = {
    role_slots: '角色数量',
    memory_count: '记忆条数',
    kb_file_count: '知识库文件',
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <div className="text-6xl">📊</div>
      <p className="mt-4 text-lg font-semibold text-gray-800">
        已达到{featureNames[feature]}上限
      </p>
      <p className="mt-2 text-sm text-gray-600">
        当前等级最多可使用 {limit === -1 ? '无限制' : limit} 个{featureNames[feature]}
      </p>
      <button
        className="mt-4 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-2 text-white font-semibold hover:from-yellow-600 hover:to-orange-600"
        onClick={() => {
          // 跳转到VIP升级页面
          window.location.href = '/vip/upgrade';
        }}
      >
        升级VIP解锁更多
      </button>
    </div>
  );
}

/**
 * VIP升级提示Banner
 */
export function VIPUpgradeBanner({ feature }: { feature: keyof VIPPermissions }) {
  const { hasPermission, getVIPLevel } = useVIPContext();

  if (hasPermission(feature)) {
    return null;
  }

  const level = getVIPLevel();

  return (
    <div className="mb-4 rounded-lg bg-gradient-to-r from-yellow-50 to-orange-50 p-4 border border-yellow-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">⭐</span>
          <div>
            <p className="font-semibold text-gray-800">升级VIP解锁此功能</p>
            <p className="text-sm text-gray-600">开通VIP会员,享受更多高级功能</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <VIPBadge level={level.level} code={level.code as any} />
          <button
            className="rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 px-4 py-2 text-sm font-semibold text-white hover:from-yellow-600 hover:to-orange-600"
            onClick={() => {
              window.location.href = '/vip/upgrade';
            }}
          >
            立即升级
          </button>
        </div>
      </div>
    </div>
  );
}
